import { makeStyles, Theme, createStyles } from "@material-ui/core";
import * as React from "react";
import SimpleTable from "../../../components/SimpleTable";
import { useHubEventDateWindow } from "../../../core/hooks/useHubHooks";
import InstallationModel from "../../../core/models/Installation";
import { InstallationLevelReadings } from "../../../core/models/InstallationLevelReadings";
import { LevelThreshold } from "../../../core/models/LevelThreshold";
import { TanksLevelsReadings } from "../../../core/models/TanksLevelsReadings";
import {
  EVENT_EXPORT_EXCEL_LEVEL_HISTORY,
  EVENT_EXPORT_PDF_LEVEL_HISTORY,
  EVENT_INSTALLATION_LEVEL_HISTORY_FILTER_DATE_CHANGE,
  EVENT_INSTALLATION_LEVEL_HISTORY_FILTER_TANK_CHANGE,
  EVENT_INSTALLATION_LEVEL_HISTORY_SPLIT_TANKS_CHANGE,
  EVENT_REFRESH_INSTALLATION_DETAIL_PAGE,
} from "../../../Events";
import {
  FETCH_EXCEL_FILE_LEVEL_HISTORY,
  FETCH_INSTALLATION_LEVEL_READINGS,
  FETCH_PDF_FILE_FROM_HTML,
  FETCH_TANKS_LEVEL_READINGS,
} from "../../../Fedx";
import { getDateFromTimezonePtBrToGMT, keepDateFromTimezonePtBr } from "../../../helpers/datetime";
import {
  calculateLevelPercentage,
  getLevelColor,
  getLevelStatusString,
} from "../../../helpers/installationTankHelpers";
import { formatValue, isArrayValid } from "../../../helpers/utility";
import { useHubx, useHubXSubscribe } from "../../../HubX";
import { InstallationLevelServiceFetchParams } from "../../../services/InstallationLevelService";
import { TankLevelServiceFetchParams } from "../../../services/TankService";
import orderBy from "lodash/orderBy";
import { ITankLevelReadings } from "../../../core/contracts/ITankLevel";
import { IInstallationLevelReadings } from "../../../core/contracts/IInstallationLevel";
import { IExportExcel } from "../../../core/contracts/IExportExcel";
import IHtml from "../../../core/contracts/IHtml";
import ReportGrid from "../../../components/Report/ReportGrid";
import { renderToString } from "react-dom/server";
import ReportSimpleTable from "../../../components/Report/ReportSimpleTable";
import useAuth from "../../../core/hooks/useAuth";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      // WARNING
      // 100vh - header - tabs - toolbar
      maxHeight: `calc(100vh - ${theme.spacing(3)}px - 67px - 77px - 16px - ${
        theme.mixins.toolbar.minHeight
      }px)`,
    },
  })
);

type InstallationLevelHistoryProps = {
  installation?: InstallationModel;
};

type ReportDataType = {
  date: string;
  level: number;
  level_in: string | number;
  status: string;
  tanqueId: string | undefined;
  pressure: number | undefined;
};

function InstallationLevelHistory({ installation }: InstallationLevelHistoryProps) {
  const [filterTankId, setFilterTankId] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [useTankData, setUseTankData] = React.useState(false);
  const [exportExcel, setExportExcel] = React.useState(false);
  const [exportPdf, setExportPdf] = React.useState(false);
  const [
    installationLevelData,
    setInstallationLevelData,
  ] = React.useState<InstallationLevelReadings | null>(null);
  const [tanksLevelData, setTanksLevelData] = React.useState<TanksLevelsReadings | null>(null);
  // eslint-disable-next-line @typescript-eslint/ban-types
  const [reportData, setReportData] = React.useState<ReportDataType[]>([]);

  const filterInterval = useHubEventDateWindow(
    EVENT_INSTALLATION_LEVEL_HISTORY_FILTER_DATE_CHANGE,
    true
  );

  const classes = useStyles();
  const hub = useHubx();
  const authProvider = useAuth();
  useHubXSubscribe(EVENT_INSTALLATION_LEVEL_HISTORY_FILTER_TANK_CHANGE, setFilterTankId);
  useHubXSubscribe(EVENT_INSTALLATION_LEVEL_HISTORY_SPLIT_TANKS_CHANGE, setUseTankData);
  useHubXSubscribe(EVENT_EXPORT_EXCEL_LEVEL_HISTORY, setExportExcel);
  useHubXSubscribe(EVENT_EXPORT_PDF_LEVEL_HISTORY, setExportPdf);
  useHubXSubscribe(EVENT_REFRESH_INSTALLATION_DETAIL_PAGE, fetchData);

  async function fetchData() {
    if (installation?.installation_id) {
      setLoading(true);

      const fetchParams: InstallationLevelServiceFetchParams = {
        installation_id: installation?.installation_id,
        startDate: filterInterval?.start || undefined,
        endDate: filterInterval?.end || undefined,
      };

      if (fetchParams.startDate && fetchParams.endDate) {
        const APIInstallationResponse: InstallationLevelReadings = await hub.request(
          FETCH_INSTALLATION_LEVEL_READINGS,
          fetchParams
        );

        if (installation?.tanks?.length && installation?.tanks?.length > 1) {
          const tanksFetchParams: TankLevelServiceFetchParams = {
            ...fetchParams,
            tanks_ids: installation.tanks.map((x) => x.tank_id),
          };

          const APITanksResponse: TanksLevelsReadings = await hub.request(
            FETCH_TANKS_LEVEL_READINGS,
            tanksFetchParams
          );
          setTanksLevelData(APITanksResponse);
        }

        setInstallationLevelData(APIInstallationResponse);
      }
      setLoading(false);
    }
  }

  async function fetchFile(exportFile: IExportExcel) {
    await hub.request(FETCH_EXCEL_FILE_LEVEL_HISTORY, exportFile);
  }

  async function fetchPdf(htmlFile: IHtml) {
    await hub.request(FETCH_PDF_FILE_FROM_HTML, htmlFile);
  }

  async function mapReportData(
    date: string,
    level: number,
    level_in: string | number,
    status: string,
    tankId: string | undefined,
    pres?: number
  ) {
    const newReportData: ReportDataType = {
      date: date,
      level: level,
      level_in: level_in,
      status: status,
      tanqueId: tankId,
      pressure: pres,
    };

    setReportData((reportData) => [...reportData, newReportData]);
  }

  const memoDataToDisplay = React.useMemo(() => {
    let data;
    let thresholds: LevelThreshold;
    let uom = "";
    setReportData([]);

    if (!useTankData && installationLevelData) {
      data = orderBy(
        installationLevelData?.readings,
        function (o) {
          return o?.reading_date ? new Date(o.reading_date).getTime() : 0;
        },
        "desc"
      );
      thresholds = {
        veryLow: installationLevelData.outage_level,
        low: installationLevelData.reserve_level,
        over: installationLevelData.installation_capacity_su,
      };
      uom = installation?.product_uom || "";
    }

    if (useTankData && isArrayValid(tanksLevelData)) {
      if (filterTankId) {
        const filteredTank = tanksLevelData?.filter((tank) => tank.tank_id === filterTankId)[0];
        if (filteredTank) {
          data = orderBy(
            filteredTank?.readings,
            function (o) {
              return o?.reading_date ? new Date(o.reading_date).getTime() : 0;
            },
            "desc"
          );
          thresholds = {
            veryLow: filteredTank?.outage_level,
            over: filteredTank?.tank_capacity_su,
          };
          uom = filteredTank?.product_uom ?? "";
        }
      } else {
        return (
          orderBy(
            tanksLevelData?.flatMap((tank) => {
              const thresholds = {
                veryLow: tank?.outage_level,
              };
              uom = tank?.product_uom ?? "";

              return tank?.readings?.map((d, idx) => {
                mapReportData(
                  keepDateFromTimezonePtBr(d.reading_date),
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  calculateLevelPercentage(d.tank_level_su, tank.tank_capacity_su!),
                  formatValue(d.tank_level_su),
                  getLevelStatusString(d.level_status || d.tank_level_su, thresholds),
                  tank?.tank_id.toString(),
                  d.tank_pressure_uom && d.tank_pressure_uom != "" ? d.tank_pressure : undefined
                );

                return {
                  sortDate: d.reading_date,
                  data:
                    d.tank_pressure_uom && d.tank_pressure_uom != "" && authProvider.hasHighLevel()
                      ? [
                          <strong key={`date_${tank?.tank_id}_${idx}`}>
                            {keepDateFromTimezonePtBr(d.reading_date)}
                          </strong>,
                          tank.tank_id,
                          calculateLevelPercentage(d.tank_level_su, tank?.tank_capacity_su),
                          `${formatValue(d.tank_level_su)}${uom ? ` ${uom}` : ""}`,
                          `${d.tank_pressure} ${d.tank_pressure_uom}`,
                          <span
                            style={{ color: getLevelColor(d.tank_level_su, thresholds) }}
                            key={`status_${tank.tank_id}_${idx}`}
                          >
                            {getLevelStatusString(d.level_status || d.tank_level_su, thresholds)}
                          </span>,
                        ]
                      : [
                          <strong key={`date_${tank?.tank_id}_${idx}`}>
                            {keepDateFromTimezonePtBr(d.reading_date)}
                          </strong>,
                          tank.tank_id,
                          calculateLevelPercentage(d.tank_level_su, tank?.tank_capacity_su),
                          `${formatValue(d.tank_level_su)}${uom ? ` ${uom}` : ""}`,
                          <span
                            style={{ color: getLevelColor(d.tank_level_su, thresholds) }}
                            key={`status_${tank.tank_id}_${idx}`}
                          >
                            {getLevelStatusString(d.level_status || d.tank_level_su, thresholds)}
                          </span>,
                        ],
                };
              });
            }),
            function (o) {
              return o?.sortDate && new Date(o?.sortDate).getTime();
            },
            "desc"
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          ).map((x) => x!.data) || []
        );
      }
    }

    if (useTankData) {
      return (
        (data as ITankLevelReadings[])?.map((d, idx) => {
          mapReportData(
            keepDateFromTimezonePtBr(d.reading_date),
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            calculateLevelPercentage(d.tank_level_su, thresholds.over!),
            formatValue(d.tank_level_su),
            getLevelStatusString(d.level_status || d.tank_level_su, thresholds),
            undefined,
            d.tank_pressure_uom && d.tank_pressure_uom != "" ? d.tank_pressure : undefined
          );

          return d.tank_pressure_uom && d.tank_pressure_uom != "" && authProvider.hasHighLevel()
            ? [
                <strong key={`date_${idx}`}>{keepDateFromTimezonePtBr(d.reading_date)}</strong>,
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                calculateLevelPercentage(d.tank_level_su, thresholds.over!),
                `${formatValue(d.tank_level_su)}${uom ? ` ${uom}` : ""}`,
                `${d.tank_pressure} ${d.tank_pressure_uom}`,
                <span
                  style={{ color: getLevelColor(d.tank_level_su, thresholds) }}
                  key={`status_${idx}`}
                >
                  {getLevelStatusString(d.level_status || d.tank_level_su, thresholds)}
                </span>,
              ]
            : [
                <strong key={`date_${idx}`}>{keepDateFromTimezonePtBr(d.reading_date)}</strong>,
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                calculateLevelPercentage(d.tank_level_su, thresholds.over!),
                `${formatValue(d.tank_level_su)}${uom ? ` ${uom}` : ""}`,
                <span
                  style={{ color: getLevelColor(d.tank_level_su, thresholds) }}
                  key={`status_${idx}`}
                >
                  {getLevelStatusString(d.level_status || d.tank_level_su, thresholds)}
                </span>,
              ];
        }) || []
      );
    }

    return (
      (data as IInstallationLevelReadings[])?.map((d, idx) => {
        mapReportData(
          keepDateFromTimezonePtBr(d.reading_date),
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          calculateLevelPercentage(d.installation_level_su, thresholds.over!),
          formatValue(d.installation_level_su),
          getLevelStatusString(d.installation_level_status || d.installation_level_su, thresholds),
          undefined,
          d.installation_pressure_uom && d.installation_pressure_uom != "" ? d.installation_pressure : undefined
        );
        return d.installation_pressure_uom &&
          d.installation_pressure_uom != "" &&
          installation?.tanks?.length == 1 &&
          authProvider.hasHighLevel()
          ? [
              <strong key={`date_${idx}`}>{keepDateFromTimezonePtBr(d.reading_date)}</strong>,
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              calculateLevelPercentage(d.installation_level_su, thresholds.over!),
              `${formatValue(d.installation_level_su)}${uom ? ` ${uom}` : ""}`,
              `${d.installation_pressure} ${d.installation_pressure_uom}`,
              <span
                style={{ color: getLevelColor(d.installation_level_su, thresholds) }}
                key={`status_${idx}`}
              >
                {getLevelStatusString(
                  d.installation_level_status || d.installation_level_su,
                  thresholds
                )}
              </span>,
            ]
          : [
              <strong key={`date_${idx}`}>{keepDateFromTimezonePtBr(d.reading_date)}</strong>,
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              calculateLevelPercentage(d.installation_level_su, thresholds.over!),
              `${formatValue(d.installation_level_su)}${uom ? ` ${uom}` : ""}`,
              <span
                style={{ color: getLevelColor(d.installation_level_su, thresholds) }}
                key={`status_${idx}`}
              >
                {getLevelStatusString(
                  d.installation_level_status || d.installation_level_su,
                  thresholds
                )}
              </span>,
            ];
      }) || []
    );
  }, [useTankData, installationLevelData, tanksLevelData, installation, filterTankId]);

  React.useEffect(() => {
    const doFetch = async () => await fetchData();
    doFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [installation?.installation_id, installation?.tanks, filterInterval]);

  const headers = React.useMemo(() => {
    let headersAux = [
      "Data",
      "% Nível",
      "Nível Instantâneo",
      (memoDataToDisplay.length ? memoDataToDisplay[0].length == 5 : false) &&
      installation?.tanks?.length == 1 &&
      authProvider.hasHighLevel()
        ? "Pressão"
        : "",
      "Status de Nível",
    ];

    if (useTankData && !filterTankId) {
      headersAux = [
        "Data",
        "ID Tanque",
        "% Nível",
        "Nível Instantâneo",
        (memoDataToDisplay.length ? memoDataToDisplay[0].length >= 5 : false) &&
        authProvider.hasHighLevel()
          ? "Pressão"
          : "",
        "Status de Nível",
      ];
    }
    return headersAux;
  }, [memoDataToDisplay]);

  const headersFileExport = React.useMemo(() => {
    let headersFileExport = [
      "Data",
      "% Nível",
      "Nível Instantâneo(" + installation?.product_uom + ")",
      (memoDataToDisplay.length ? memoDataToDisplay[0].length >= 5 : false) &&
      authProvider.hasHighLevel()
        ? "Pressão"
        : "",
      "Status de Nível",
    ];

    if (useTankData && !filterTankId) {
      headersFileExport = [
        "Data",
        "ID Tanque",
        "% Nível",
        "Nível Instantâneo(" + installation?.product_uom + ")",
        (memoDataToDisplay.length ? memoDataToDisplay[0].length >= 5 : false) &&
        authProvider.hasHighLevel()
          ? "Pressão"
          : "",
        "Status de Nível",
      ];
    }
    return headersFileExport;
  }, [memoDataToDisplay]);

  if (exportExcel) {
    // Executa aqui
    if (reportData) {
      const exportFile: IExportExcel = {
        headers: headersFileExport,
        data: reportData,
        fileName: installation?.product_code
          ? "Historico_Nivel_" + installation.product_code + " #" + installation.installation_id
          : "Historico_Nivel",
      };

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      fetchFile(exportFile);
    }

    setExportExcel(false);
  }

  if (exportPdf) {
    const exportFile: IHtml = {
      htmlString: renderToString(
        <ReportGrid headerReport="Histórico de Nível">
          <ReportSimpleTable rows={memoDataToDisplay} header={headers} />
        </ReportGrid>
      ),
      fileName: installation?.product_code
        ? "Histórico_Nivel_" + installation.product_code + " #" + installation.installation_id
        : "Histórico_Nivel",
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    fetchPdf(exportFile);

    setExportPdf(false);
  }

  return (
    <div className={classes.root}>
      <SimpleTable header={headers} rows={memoDataToDisplay} loading={loading} />
    </div>
  );
}

export default InstallationLevelHistory;
